import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#attachMoreIndicator();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#detachMoreIndicator();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #attachMoreIndicator() {
    const scroller = this.querySelector('.cart-drawer__content');
    const indicator = this.querySelector('[data-cart-more-indicator]');

    if (!scroller || !indicator) return;

    this.#moreIndicator = { scroller, indicator };
    this.#moreIndicatorObserver = new ResizeObserver(this.#updateMoreIndicator);
    scroller.addEventListener('scroll', this.#updateMoreIndicator);
    indicator.addEventListener('click', this.#handleIndicatorClick);
    this.#moreIndicatorObserver.observe(scroller);
    this.#updateMoreIndicator();
  }

  #detachMoreIndicator() {
    if (!this.#moreIndicator) return;

    this.#moreIndicator.scroller.removeEventListener('scroll', this.#updateMoreIndicator);
    this.#moreIndicator.indicator.removeEventListener('click', this.#handleIndicatorClick);
    this.#moreIndicatorObserver?.disconnect();
    this.#moreIndicatorObserver = null;
    this.#moreIndicator = null;
  }

  #updateMoreIndicator = () => {
    if (!this.#moreIndicator) return;

    const { scroller, indicator } = this.#moreIndicator;
    const rows = scroller.querySelectorAll('.cart-items__table-row');
    if (rows.length <= 1) {
      indicator.setAttribute('hidden', 'hidden');
      return;
    }

    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    const hide = maxScroll <= 4 || scroller.scrollTop >= maxScroll - 16;
    indicator.toggleAttribute('hidden', hide);
  };

  #handleIndicatorClick = () => {
    if (!this.#moreIndicator) return;
    const { scroller } = this.#moreIndicator;
    const rows = [...scroller.querySelectorAll('.cart-items__table-row')];
    if (!rows.length) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const summary = scroller.querySelector('.cart-drawer__summary');
    const visibleBottom = summary ? summary.getBoundingClientRect().top : scrollerRect.bottom;
    const nextRow = rows.find((row) => row.getBoundingClientRect().bottom > visibleBottom + 4);

    if (nextRow) {
      const rowRect = nextRow.getBoundingClientRect();
      const scrollTarget = rowRect.top - scrollerRect.top + scroller.scrollTop;
      scroller.scrollTo({
        top: scrollTarget,
        behavior: 'smooth',
      });
      return;
    }

    scroller.scrollBy({
      top: scroller.clientHeight * 0.8,
      behavior: 'smooth',
    });
  };

  #moreIndicator = null;
  #moreIndicatorObserver = null;
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
